import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import type { Field } from 'factorial-form'
import xor from 'lodash/xor'

import { observer } from 'mobx-react'

import type { FilterOption } from 'modules/core/components/Filters/computeOptions'
import computeOptions from 'modules/core/components/Filters/computeOptions'
import Input from 'modules/core/components/Inputs/Text'
import Popover from 'modules/core/components/PopoverMenu'
import SimpleUserSelector from 'modules/core/components/SimpleUserSelector'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import i18n from 'modules/core/lib/i18n'
import Users from 'modules/core/store/collections/users'
import type User from 'modules/core/store/models/User'

import { useNineGridBox } from 'modules/competencies/lib/ninegridboxContext'

interface Props {
  label: string
  users: User[]
  selectedIds: number[]
  multiselect?: boolean
}

export const UserSelectorInput = observer(
  ({ label, selectedIds, users, multiselect = true }: Props) => {
    const { getUserCompetencies } = useNineGridBox()
    const [ids, setIds] = React.useState(selectedIds)
    const count = ids.length

    const user = Users.find({
      id: ids[0],
    })?.fullName()

    const textLabel = count
      ? i18n.tp('user_selector.label', {
          count,
          user,
        })
      : i18n.t('user_selector.label.zero')

    const submitLabel = count
      ? i18n.tp('user_selector.submit', {
          count,
        })
      : i18n.t('user_selector.submit.zero')

    const onSubmited = () => {
      getUserCompetencies(ids)
    }

    const handleSelect = (id: number | null | undefined) => {
      const newIds =
        !multiselect && !ids.includes(id as number)
          ? [id].filter(Boolean)
          : xor(ids, [id].filter(Boolean))
      setIds(newIds as number[])
    }

    const [computedOptions, setComputedOptions] = React.useState<Record<string, FilterOption[]>>({})

    const { loading } = useAsyncData(async () => {
      setComputedOptions(
        await computeOptions({
          hideFilterByStatus: true,
          showFilterByTeam: true,
          showFilterByActive: true,
        })
      )
    }, [])

    return (
      <Popover type="dialog">
        <Popover.Trigger>
          <Input
            label={label}
            field={
              {
                value: textLabel,
              } as Field
            }
            select
          />
        </Popover.Trigger>
        <Popover.Content
          side="bottom"
          align="center"
          width="s360"
          onInteractOutside={() => onSubmited()}
        >
          <Box padding="s32" gap="s24">
            <SimpleUserSelector
              loadingOptions={loading}
              users={users}
              onSelect={handleSelect}
              onSelectMultiple={multiselect ? setIds : null}
              selectedIds={ids}
              options={computedOptions}
            />
            <Box alignItems="flexStart">
              <Popover.Close>
                <Button onClick={onSubmited} disabled={!count}>
                  {submitLabel}
                </Button>
              </Popover.Close>
            </Box>
          </Box>
        </Popover.Content>
      </Popover>
    )
  }
)
